import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { withTranslation } from "react-i18next";
import QuestionTitle from "./Title";
import Help from "./Help";

const getLuminance = hex => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};

const getTextColor = hex => (hex && getLuminance(hex) > 0.55 ? "#333333" : "#ffffff");

/**
 * Fallback for any choice that configures no `color` of its own.
 *
 * Deliberately neutral. A green-to-red ramp asserts clinical meaning — fine at one end, severe
 * at the other — and that judgement belongs to the survey defining the scale, not to the
 * component drawing it. A scale that wants a ramp declares one hex per choice in its seeder
 * (see the MSK and Presenteeism seeders); this grey is only what an undeclared scale falls to.
 */
const NEUTRAL = "#e0e0e0";

// An explicitly configured colour always wins; the neutral only fills the gaps, per choice, so a
// partially coloured scale keeps the colours it does declare.
const resolveColors = choices => choices.map(choice => choice.color || NEUTRAL);

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 0 16px;
`;

const TrackWrap = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    height: 96px;
`;

const Track = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 6px;
    border-radius: 99px;
    background-image: linear-gradient(to right, ${props => props.gradient});
    opacity: 0.3;
`;

const Buttons = styled.div`
    position: relative;
    z-index: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`;

const Circle = styled.button`
    width: 72px;
    height: 72px;
    border-radius: 50%;
    border: 3px solid #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: "DM Mono", monospace, sans-serif;
    font-size: 20px;
    font-weight: 500;
    background-color: ${props => props.bgColor};
    color: ${props => props.textColor};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18), 0 0 0 3px #fff;
    transition: transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.18s ease;
    flex-shrink: 0;
    -webkit-tap-highlight-color: transparent;
    user-select: none;
    outline: none;

    &:hover {
        transform: scale(1.1);
        z-index: 2;
    }

    ${props =>
        props.selected &&
        `
        transform: scale(1.2);
        box-shadow: 0 6px 20px rgba(0,0,0,0.22), 0 0 0 4px #fff, 0 0 0 7px ${props.bgColor};
        z-index: 3;
    `}
`;

const Endpoints = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-top: 12px;
    padding: 0 4px;
`;

const EndpointLabel = styled.span`
    font-size: 12px;
    color: #6b7a8d;
    font-weight: 500;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 5px;
`;

const Dot = styled.span`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: ${props => props.color};
    flex-shrink: 0;
`;

const PainScaleInput = ({ model, selected, onSelect, t }) => {
    const [localSelected, setLocalSelected] = useState(selected || null);

    const choices = model.getChoices();
    const helpText = model.getHelpText();
    const title = model.getDisplayText();

    const handleClick = label => {
        setLocalSelected(label);
        onSelect(label);
    };

    const colors = resolveColors(choices);

    // Only the two ends of the scale are ever captioned. Reading helpText from the first and
    // last choice — rather than from a fixed pair of keys — is what lets one component serve
    // any scale; helpText on the choices in between has no meaning here and is ignored.
    const lastIndex = choices.length - 1;
    const lowCaption = choices[0] && choices[0].helpText;
    const highCaption = lastIndex > 0 && choices[lastIndex] ? choices[lastIndex].helpText : "";
    const hasCaptions = Boolean(lowCaption || highCaption);

    // linear-gradient needs two stops to be valid CSS, so a degenerate one-point scale repeats
    // its single colour rather than emitting a rule the browser throws away.
    const gradient = (colors.length > 1 ? colors : [...colors, ...colors]).join(", ");

    return (
        <Wrapper>
            <QuestionTitle htmlFor={"pain-scale"}>{t(title)}</QuestionTitle>
            {helpText && <Help>{t(helpText)}</Help>}
            <TrackWrap>
                <Track data-testid={"pain-scale-track"} gradient={gradient} />
                <Buttons>
                    {choices.map((choice, index) => {
                        const label = t(choice.label, choice.label);

                        return (
                            <Circle
                                key={index}
                                bgColor={colors[index]}
                                textColor={getTextColor(colors[index])}
                                selected={localSelected === choice.label}
                                onClick={() => handleClick(choice.label)}
                                aria-label={label}
                                aria-pressed={localSelected === choice.label}
                            >
                                {label}
                            </Circle>
                        );
                    })}
                </Buttons>
            </TrackWrap>
            {hasCaptions && (
                // Both labels are rendered whenever either caption exists: the empty one holds
                // its side of the space-between row so a lone high caption stays right-aligned.
                <Endpoints data-testid={"pain-scale-endpoints"}>
                    <EndpointLabel>
                        {lowCaption && <Dot color={colors[0]} />}
                        {lowCaption ? t(lowCaption) : ""}
                    </EndpointLabel>
                    <EndpointLabel>
                        {highCaption && <Dot color={colors[lastIndex]} />}
                        {highCaption ? t(highCaption) : ""}
                    </EndpointLabel>
                </Endpoints>
            )}
        </Wrapper>
    );
};

PainScaleInput.propTypes = {
    model: PropTypes.object.isRequired,
    selected: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired
};

export default withTranslation()(PainScaleInput);
